<script setup lang="ts">
import { checkEnvSite } from "~~/server/utils/getAlgoliaConfig"
import { buildExcludeFilter } from "~/utils/algolia"
import { BrandSubListingProps } from "./BrandSubListing.props"

const props = defineProps<BrandSubListingProps>()

const route = useRoute()
const { setPageInfo } = usePageInfo()
const aisComponent = ref(null)
const isAisComponentRendered = ref(false)

setPageInfo({
  ...props.pageInfo,
  loadPage: props.loadPage,
  listingType: "brandCategory",
  pageTitle: props.pageInfo.mainTitle
})

useState(
  `breadcrumbs-length-${route.path}`,
  () => props.breadcrumbs?.links.length
)

const serverRendered = useState(`server-rendered-${route.path}`, () => false)
onServerPrefetch(() => {
  serverRendered.value = true
})

watch(
  () => aisComponent.value,
  (newValue) => {
    isAisComponentRendered.value = !!newValue
  },
  { immediate: true }
)

const breadcrumbs = computed(() => {
  return {
    ...props.breadcrumbs,
    hideFirstItemInMobile: true,
    links: [{ label: "Home", to: "/" }, ...props.breadcrumbs?.links]
  }
})
</script>

<template>
  <div class="wrapper_brand-detail grid-standard">
    <!-- Breadcrumbs -->
    <UtilsBreadcrumbs
      v-if="breadcrumbs"
      v-bind="breadcrumbs"
      class="negative-margined col-span-full hidden md:block"
    />
    <div
      v-if="pageInfo?.heroImg"
      class="
        col-span-full
        mb-6
        hidden
        overflow-hidden
        md:relative
        md:flex
        md:aspect-[4/1]
      "
    >
      <UtilsWrapperImage
        v-bind="pageInfo?.heroImg"
        :preload="true"
        :img-attrs="{ fetchpriority: 'high' }"
        class="
          hero-image__desktop
          absolute
          left-0
          top-1/2
          w-full
          -translate-y-1/2
        "
      />
    </div>
    <div
      v-if="pageInfo?.heroImgMobile"
      class="cover-image__mobile negative-margined col-span-full mb-6 md:hidden"
    >
      <UtilsWrapperImage
        v-bind="pageInfo?.heroImgMobile"
        :preload="true"
        :img-attrs="{ fetchpriority: 'high' }"
        class="wrapper-image-fixed-240 wrapper-image-full-container-centered"
      />
    </div>
    <div class="col-span-full mb-6 hidden md:block" v-if="!pageInfo.brandInfos">
      <h1 class="mammoth-bold" :class="{ 'pb-4': true }">
        {{ pageInfo.mainTitle }}
      </h1>
    </div>
    <div
      v-if="pageInfo.brand?.brandLogo && pageInfo.brand?.showBrandLogo"
      class="col-span-full mb-10 mt-14 flex justify-center"
    >
      <UtilsWrapperImage
        v-bind="pageInfo.brand?.brandLogo"
        :preload="true"
        :img-attrs="{ fetchpriority: 'high' }"
      />
    </div>
    <div
      class="
        products-block-brands-utils-info
        col-span-full
        mb-0
        md:col-span-10 md:col-start-2
      "
      :class="{ 'mt-10': !pageInfo.brand?.brandLogo }"
      v-if="pageInfo.brandInfos"
    >
      <ProductBlocksBrandsUtilsInfo
        v-bind="pageInfo.brandInfos"
        :title="pageInfo.brandInfos?.title ?? pageInfo.mainTitle"
        :isBrandTitle="true"
      />
    </div>
    <div class="col-span-full">
      <DynamicContent
        v-if="content"
        class="cms-content grid-container"
        :components="content"
      />
    </div>
    <!-- TODO: footerbanner to make connector -->
    <!-- <div class="grid-standard my-6">
        <BannerDiscountPromoHorizontal
          class="col-span-full"
          v-bind="BannerDiscountStory.args"
        />
      </div> -->
    <TextBlocksCategory
      v-if="pageInfo.footerText"
      :description="pageInfo.footerText"
      class="negative-margined col-span-full"
    />
  </div>
</template>
<style lang="scss" scoped>
.cms-content {
  :deep(.cms-products-category) {
    @apply col-span-full;
  }

  :deep(.cms-brand-infos) {
    @apply col-span-full;
  }

  :deep(.cms-component-algolia-rails-products) {
    @apply col-span-full mx-[-16px] md:mx-[-71px] lg:mx-[-228px];
  }

  :deep(.cms-wrapper-video) {
    @apply col-span-full;
  }

  :deep(.cms-brand-descriptions) {
    @apply col-span-full;
  }

  :deep(.cms-hero-product) {
    @apply col-span-full;
  }

  :deep(.cms-brand-news) {
    @apply col-span-full;
  }

  :deep(.cms-brand-products-banner) {
    @apply col-span-full;
  }
}
</style>
